import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Mail, Linkedin, Github, Phone, Send, Copy, Check, ArrowUpRight } from "lucide-react";
import SectionHeading from "./SectionHeading";
import { personalInfo } from "../data/mock";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CONTACT_API = `${BACKEND_URL}/api/contact`;

const ContactCard = ({ icon: Icon, label, value, href, onCopy, copied }) => (
  <div className="group relative p-5 rounded-2xl glass hover:border-cyan-400/30 transition-colors">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-cyan-300">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500">
          {label}
        </p>
        <p className="text-sm text-white truncate">{value}</p>
      </div>
      <div className="flex items-center gap-1">
        {onCopy && (
          <button
            onClick={onCopy}
            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400 hover:text-cyan-300 transition-colors"
            aria-label="Copy"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-300" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        )}
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400 hover:text-cyan-300 transition-colors"
          >
            <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
    </div>
  </div>
);

const Contact = () => {
  const [copiedField, setCopiedField] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const copy = (field, text) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success(`${field} copied to clipboard`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(CONTACT_API, {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      toast.success(res.data?.message || "Message sent! I'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail =
        err.response?.data?.detail ||
        "Could not send message. Please try again or email directly.";
      toast.error(typeof detail === "string" ? detail : "Could not send message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent" />
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeading
          eyebrow="Contact"
          title="Let&rsquo;s build something impactful."
          description="Got a backend role, a project idea, or just want to talk distributed systems? My inbox is open."
        />

        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-3">
            <ContactCard
              icon={Mail}
              label="Email"
              value={personalInfo.email}
              href={`mailto:${personalInfo.email}`}
              onCopy={() => copy("Email", personalInfo.email)}
              copied={copiedField === "Email"}
            />
            <ContactCard
              icon={Phone}
              label="Phone"
              value={personalInfo.phone}
              href={`tel:${personalInfo.phone}`}
              onCopy={() => copy("Phone", personalInfo.phone)}
              copied={copiedField === "Phone"}
            />
            <ContactCard
              icon={Linkedin}
              label="LinkedIn"
              value="vedant-bora-79b73a3a0"
              href={personalInfo.linkedin}
            />
            <ContactCard
              icon={Github}
              label="GitHub"
              value="vedbora"
              href={personalInfo.github}
            />
          </div>

          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-3 p-6 md:p-8 rounded-3xl glass-strong relative overflow-hidden"
          >
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
            <div className="relative">
              <h3 className="font-display text-xl font-semibold text-white mb-1">
                Send a message
              </h3>
              <p className="text-sm text-zinc-400 mb-6">
                Quick note, opportunity, or feedback — all welcome.
              </p>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 block mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full h-11 px-4 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/50 transition-colors"
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 block mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full h-11 px-4 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/50 transition-colors"
                    placeholder="jane@company.com"
                  />
                </div>
              </div>
              <div className="mb-5">
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 block mb-2">
                  Message
                </label>
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/50 transition-colors resize-none"
                  placeholder="Tell me about the role / project..."
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-6 h-12 rounded-xl bg-cyan-400 hover:bg-cyan-300 disabled:opacity-50 text-zinc-950 font-semibold transition-colors"
              >
                {submitting ? "Sending..." : "Send Message"}
                <Send className="w-4 h-4" />
              </button>
              <p className="mt-3 text-[11px] text-zinc-600 font-mono">
                Messages are delivered to {personalInfo.email}.
              </p>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
